/**
 * Generate Industry Research Page
 * Two input methods: Manual Entry or Search via Company
 */

import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  Building2,
  Search,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Info,
  X,
} from 'lucide-react';

import {
  checkOrCreateIndustry,
  generateIndustryResearch,
} from '../lib/api/industryResearchApi';
import { standaloneCompaniesAPI, indiaCompanyAPI } from '../lib/api';
import { searchCompaniesInDatabase } from '../lib/api/financialApi';
import { useLiveCountriesStore } from '../stores/liveCountriesStore';
import Select from '../components/ui/Select';
import type { CheckOrCreateIndustryRequest } from '../types/industryResearch';

type InputMethod = 'manual' | 'company-search';
type SearchCountry = 'us' | 'india' | 'uk';

interface CountryOption {
  value: SearchCountry;
  label: string;
  searchPlaceholder: string;
}

const SEARCH_COUNTRIES: CountryOption[] = [
  {
    value: 'us',
    label: '🇺🇸 United States (SEC EDGAR)',
    searchPlaceholder: 'Search by company name or ticker (min 4 characters)…',
  },
  {
    value: 'india',
    label: '🇮🇳 India (NSE / BSE)',
    searchPlaceholder: 'Search by company name (min 4 characters)…',
  },
  {
    value: 'uk',
    label: '🇬🇧 United Kingdom (Companies House)',
    searchPlaceholder: 'Search UK companies by name (min 4 characters)…',
  },
];

interface CompanySearchResult {
  cik: string;
  ticker: string;
  name: string;
  industry: string;
  sicCodes?: string[];
  naicsCodes?: string[];
  gicsCode?: string;
}

interface IndiaSearchResult {
  stock_name: string;
  sc_id: string | null;
  sc_nse_symbol?: string | null;
  sc_bse_symbol?: string | null;
  [key: string]: any;
}

const GenerateIndustryResearch: React.FC = () => {
  const navigate = useNavigate();
  const [inputMethod, setInputMethod] = useState<InputMethod>('manual');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdIndustry, setCreatedIndustry] = useState<{
    industryId: string;
    name: string;
    existed: boolean;
  } | null>(null);

  // Form state
  const [formData, setFormData] = useState<CheckOrCreateIndustryRequest>({
    name: '',
    category: '',
    sicCodes: [],
    naicsCodes: [],
    gicsCode: '',
    source: 'manual',
    geographicScope: 'Global',
  });

  const liveCountries = useLiveCountriesStore((s) => s.liveCountries);

  // Company search state
  const [searchCountry, setSearchCountry] = useState<SearchCountry>('us');
  const [companySearch, setCompanySearch] = useState('');
  const [companyResults, setCompanyResults] = useState<CompanySearchResult[]>([]);
  const [indiaResults, setIndiaResults] = useState<IndiaSearchResult[]>([]);
  const [ukResults, setUkResults] = useState<{ company_name: string; years_count: number; sector: string }[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<CompanySearchResult | null>(null);
  const [selectedIndiaCompany, setSelectedIndiaCompany] = useState<IndiaSearchResult | null>(null);
  const [selectedUkCompany, setSelectedUkCompany] = useState<{ company_name: string; years_count: number; sector: string } | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Input chip state
  const [sicInput, setSicInput] = useState('');
  const [naicsInput, setNaicsInput] = useState('');

  // Duplicate warning
  const [duplicateWarning, setDuplicateWarning] = useState('');

  // Check or Create Mutation
  const checkOrCreateMutation = useMutation({
    mutationFn: (request: CheckOrCreateIndustryRequest) =>
      checkOrCreateIndustry(request, 'user_001'),
    onSuccess: (data) => {
      setCreatedIndustry({
        industryId: data.industryId,
        name: data.name,
        existed: data.existed,
      });
      setShowSuccessModal(true);
    },
  });

  // Generate Research Mutation
  const generateResearchMutation = useMutation({
    mutationFn: (industryId: string) =>
      generateIndustryResearch(industryId, false, 'user_001'),
    onSuccess: (data) => {
      navigate(`/industries/${data.industryId}`);
    },
  });

  const handleAddSicCode = () => {
    if (sicInput.trim()) {
      const codes = sicInput.split(',').map((c) => c.trim()).filter(Boolean);
      setFormData((prev) => ({
        ...prev,
        sicCodes: [...(prev.sicCodes || []), ...codes],
      }));
      setSicInput('');
    }
  };

  const handleRemoveSicCode = (code: string) => {
    setFormData((prev) => ({
      ...prev,
      sicCodes: (prev.sicCodes || []).filter((c) => c !== code),
    }));
  };

  const handleAddNaicsCode = () => {
    if (naicsInput.trim()) {
      const codes = naicsInput.split(',').map((c) => c.trim()).filter(Boolean);
      setFormData((prev) => ({
        ...prev,
        naicsCodes: [...(prev.naicsCodes || []), ...codes],
      }));
      setNaicsInput('');
    }
  };

  const handleRemoveNaicsCode = (code: string) => {
    setFormData((prev) => ({
      ...prev,
      naicsCodes: (prev.naicsCodes || []).filter((c) => c !== code),
    }));
  };

  const handleCompanySearch = async () => {
    if (!companySearch.trim()) {
      return;
    }

    setIsSearching(true);
    setSearchError(null);
    setCompanyResults([]);
    setIndiaResults([]);
    setUkResults([]);
    setSelectedCompany(null);
    setSelectedIndiaCompany(null);
    setSelectedUkCompany(null);

    try {
      if (searchCountry === 'india') {
        const res = await indiaCompanyAPI.searchCompanies(companySearch);
        const results = res.results || [];
        setIndiaResults(results);
        if (results.length === 0) {
          setSearchError('No companies found. Try a different company name.');
        }
      } else if (searchCountry === 'uk') {
        const res = await searchCompaniesInDatabase(companySearch, 10);
        const results = (res.results || []).filter((c) => c.country === 'UK');
        setUkResults(results);
        if (results.length === 0) {
          setSearchError('No UK companies found in the platform. Process a UK company first via Company Research.');
        }
      } else {
        const results = await standaloneCompaniesAPI.searchSECCompanies(companySearch);
        const transformedResults: CompanySearchResult[] = (results || []).map((company: any) => ({
          cik: company.cik,
          ticker: company.ticker || '',
          name: company.name,
          industry: '',
          sicCodes: [],
          naicsCodes: [],
          gicsCode: '',
        }));
        setCompanyResults(transformedResults);
        if (transformedResults.length === 0) {
          setSearchError('No companies found matching your search. Try a different company name or ticker (minimum 4 characters).');
        }
      }
    } catch (error) {
      console.error('Company search failed:', error);
      setSearchError('Failed to search. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectCompany = async (company: CompanySearchResult) => {
    setSelectedCompany(company);
    setSearchError(null);

    // Fetch detailed company info (including SIC) from SEC
    try {
      const companyInfo = await standaloneCompaniesAPI.getCompanyBasicInfo(company.cik);

      // Update selected company with fetched SIC info
      const updatedCompany = {
        ...company,
        industry: companyInfo.industry || companyInfo.sicDescription || 'Unknown Industry',
        sicCodes: companyInfo.sic ? [companyInfo.sic] : [],
      };
      setSelectedCompany(updatedCompany);

      // Derive category from SIC description
      const category = deriveCategory(companyInfo.industry || '');

      setFormData({
        name: updatedCompany.industry,
        category: category,
        sicCodes: updatedCompany.sicCodes || [],
        naicsCodes: [], // SEC doesn't provide NAICS
        gicsCode: '', // SEC doesn't provide GICS
        source: 'company_search',
        geographicScope: 'Global',
      });
    } catch (error) {
      console.error('Failed to fetch company details:', error);
      // Still allow selection with basic info
      setFormData({
        name: company.name + ' Industry', // Fallback
        category: 'Technology',
        sicCodes: [],
        naicsCodes: [],
        gicsCode: '',
        source: 'company_search',
        geographicScope: 'Global',
      });
    }
  };

  const handleSelectIndiaCompany = (company: IndiaSearchResult) => {
    setSelectedIndiaCompany(company);
    setFormData({
      name: company.stock_name,
      category: 'Other',
      sicCodes: [],
      naicsCodes: [],
      gicsCode: '',
      source: 'company_search',
      geographicScope: 'India',
    });
  };

  const handleSelectUkCompany = (company: { company_name: string; years_count: number; sector: string }) => {
    setSelectedUkCompany(company);
    setFormData({
      name: company.company_name,
      category: company.sector || 'Other',
      sicCodes: [],
      naicsCodes: [],
      gicsCode: '',
      source: 'company_search',
      geographicScope: 'United Kingdom',
    });
  };

  // Derive category from SIC description
  const deriveCategory = (sicDescription: string): string => {
    const description = sicDescription.toLowerCase();
    if (description.includes('software') || description.includes('computer') || description.includes('electronic')) {
      return 'Technology';
    }
    if (description.includes('pharmaceutical') || description.includes('health') || description.includes('medical')) {
      return 'Healthcare';
    }
    if (description.includes('bank') || description.includes('insurance') || description.includes('financial')) {
      return 'Finance';
    }
    if (description.includes('retail') || description.includes('store') || description.includes('merchant')) {
      return 'Retail';
    }
    if (description.includes('manufacturing') || description.includes('industrial')) {
      return 'Manufacturing';
    }
    if (description.includes('energy') || description.includes('oil') || description.includes('gas') || description.includes('electric')) {
      return 'Energy';
    }
    if (description.includes('telecom') || description.includes('communication')) {
      return 'Telecommunications';
    }
    if (description.includes('transport') || description.includes('airline') || description.includes('logistics')) {
      return 'Transportation';
    }
    if (description.includes('real estate') || description.includes('property')) {
      return 'Real Estate';
    }
    return 'Other';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      alert('Industry name is required');
      return;
    }

    // Clear duplicate warning
    setDuplicateWarning('');

    // Submit
    checkOrCreateMutation.mutate(formData);
  };

  const handleGenerateNow = () => {
    if (createdIndustry) {
      generateResearchMutation.mutate(createdIndustry.industryId);
      setShowSuccessModal(false);
    }
  };

  const handleGenerateLater = () => {
    setShowSuccessModal(false);
    navigate('/industries');
  };

  const handleViewExisting = () => {
    if (createdIndustry) {
      navigate(`/industries/${createdIndustry.industryId}`);
    }
  };

  // Show inline progress page while research is generating
  if (generateResearchMutation.isPending) {
    return (
      <div className="p-8 max-w-4xl mx-auto">
        <div className="flex flex-col items-center justify-center py-24 gap-6">
          <Loader2 className="w-12 h-12 animate-spin text-primary-600" />
          <div className="text-center">
            <h2 className="text-xl font-semibold text-neutral-800 mb-2">
              Generating Industry Research
            </h2>
            <p className="text-neutral-500 mb-1">
              Analyzing market data, competitive landscape, and financial benchmarks...
            </p>
            <p className="text-sm text-neutral-400">
              This may take 2-5 minutes. You can navigate away — research will continue in the background.
            </p>
          </div>
          <div className="w-64 h-1.5 bg-neutral-200 rounded-full overflow-hidden">
            <div className="h-full bg-primary-500 rounded-full animate-pulse" style={{ width: '60%' }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-neutral-900 flex items-center gap-3 mb-2">
          <TrendingUp className="w-8 h-8 text-primary-600" />
          Add New Industry
        </h1>
        <p className="text-neutral-600">
          Create an industry stub with optional immediate research generation
        </p>
      </div>

      {/* Input Method Toggle */}
      <div className="bg-white border border-neutral-200 rounded-lg p-6 mb-6">
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setInputMethod('manual')}
            className={`flex-1 px-6 py-3 rounded-lg font-medium transition-colors ${
              inputMethod === 'manual'
                ? 'bg-primary-600 text-white'
                : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
            }`}
          >
            Manual Entry
          </button>
          <button
            onClick={() => setInputMethod('company-search')}
            className={`flex-1 px-6 py-3 rounded-lg font-medium transition-colors ${
              inputMethod === 'company-search'
                ? 'bg-primary-600 text-white'
                : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
            }`}
          >
            <Building2 className="w-5 h-5 inline mr-2" />
            Search via Company
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Manual Entry Method */}
          {inputMethod === 'manual' && (
            <>
              {/* Industry Name */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Industry Name <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="e.g., Consumer Electronics, Cloud Computing, Renewable Energy"
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      category: e.target.value,
                    }))
                  }
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Select category</option>
                  <option value="Technology">Technology</option>
                  <option value="Healthcare">Healthcare</option>
                  <option value="Finance">Finance</option>
                  <option value="Manufacturing">Manufacturing</option>
                  <option value="Energy">Energy</option>
                  <option value="Retail">Retail</option>
                  <option value="Telecommunications">Telecommunications</option>
                  <option value="Transportation">Transportation</option>
                  <option value="Real Estate">Real Estate</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Classification Codes */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-2 mb-4">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-blue-900 mb-1">
                      Industry Classification Codes (Optional)
                    </h4>
                    <p className="text-sm text-blue-700">
                      These codes help us avoid creating duplicate industries
                      and improve data quality
                    </p>
                  </div>
                </div>

                {/* SIC Codes */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    SIC Codes (comma-separated)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={sicInput}
                      onChange={(e) => setSicInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddSicCode();
                        }
                      }}
                      placeholder="e.g., 7372, 7373"
                      className="flex-1 px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={handleAddSicCode}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                    >
                      Add
                    </button>
                  </div>
                  {formData.sicCodes && formData.sicCodes.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.sicCodes.map((code) => (
                        <span
                          key={code}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-neutral-200 text-neutral-800 rounded-full text-sm"
                        >
                          {code}
                          <button
                            type="button"
                            onClick={() => handleRemoveSicCode(code)}
                            className="hover:text-red-600"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* NAICS Codes */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    NAICS Codes (comma-separated)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={naicsInput}
                      onChange={(e) => setNaicsInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddNaicsCode();
                        }
                      }}
                      placeholder="e.g., 518210, 541511"
                      className="flex-1 px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={handleAddNaicsCode}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                    >
                      Add
                    </button>
                  </div>
                  {formData.naicsCodes && formData.naicsCodes.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.naicsCodes.map((code) => (
                        <span
                          key={code}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-neutral-200 text-neutral-800 rounded-full text-sm"
                        >
                          {code}
                          <button
                            type="button"
                            onClick={() => handleRemoveNaicsCode(code)}
                            className="hover:text-red-600"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* GICS Code */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    GICS Code
                  </label>
                  <input
                    type="text"
                    value={formData.gicsCode}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        gicsCode: e.target.value,
                      }))
                    }
                    placeholder="e.g., 45103010"
                    className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Geographic Scope */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Geographic Scope
                </label>
                <select
                  value={formData.geographicScope}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      geographicScope: e.target.value,
                    }))
                  }
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="Global">Global</option>
                  <optgroup label="Country">
                    <option value="India">India</option>
                    <option value="United Kingdom">United Kingdom</option>
                    <option value="United States">United States</option>
                  </optgroup>
                  <optgroup label="Continent / Region">
                    <option value="North America">North America</option>
                    <option value="Europe">Europe</option>
                    <option value="Asia-Pacific">Asia-Pacific</option>
                    <option value="Latin America">Latin America</option>
                    <option value="Middle East & Africa">
                      Middle East & Africa
                    </option>
                  </optgroup>
                </select>
              </div>
            </>
          )}

          {/* Company Search Method */}
          {inputMethod === 'company-search' && (
            <>
              {/* Country selector (dropdown — matches Add Competitor flow) */}
              <div className="max-w-sm">
                <Select
                  label="Country / Exchange"
                  value={searchCountry}
                  onChange={(e) => {
                    setSearchCountry(e.target.value as SearchCountry);
                    setCompanySearch('');
                    setCompanyResults([]);
                    setIndiaResults([]);
                    setUkResults([]);
                    setSelectedCompany(null);
                    setSelectedIndiaCompany(null);
                    setSelectedUkCompany(null);
                    setSearchError(null);
                  }}
                  options={SEARCH_COUNTRIES.filter((c) => {
                    if (c.value === 'uk') return liveCountries.uk;
                    if (c.value === 'india') return liveCountries.india;
                    return true;
                  }).map((c) => ({ value: c.value, label: c.label }))}
                />
              </div>

              {/* Company Search */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Search Company
                </label>
                <p className="text-sm text-neutral-500 mb-2">
                  {searchCountry === 'india'
                    ? 'Search NSE/BSE listed companies by name'
                    : searchCountry === 'uk'
                    ? 'Search UK companies already in the platform by name'
                    : 'Search SEC EDGAR database by company name or ticker symbol'}
                </p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={companySearch}
                    onChange={(e) => setCompanySearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleCompanySearch();
                      }
                    }}
                    placeholder={
                      SEARCH_COUNTRIES.find((c) => c.value === searchCountry)?.searchPlaceholder
                        ?? 'Search by company name or ticker…'
                    }
                    className="flex-1 px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    disabled={isSearching}
                  />
                  <button
                    type="button"
                    onClick={handleCompanySearch}
                    disabled={isSearching || !companySearch.trim()}
                    className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSearching ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Searching...
                      </>
                    ) : (
                      <>
                        <Search className="w-5 h-5" />
                        Search
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Search Error */}
              {searchError && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-yellow-700">{searchError}</p>
                </div>
              )}

              {/* US Company Results */}
              {companyResults.length > 0 && (
                <div className="space-y-3">
                  <p className="text-sm text-neutral-500">
                    Found {companyResults.length} companies. Click to select and load industry details.
                  </p>
                  {companyResults.map((company) => (
                    <div
                      key={company.cik}
                      onClick={() => handleSelectCompany(company)}
                      className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                        selectedCompany?.cik === company.cik
                          ? 'border-primary-600 bg-primary-50'
                          : 'border-neutral-200 hover:border-neutral-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-neutral-900">
                            {company.name} {company.ticker && `(${company.ticker})`}
                          </h4>
                          <p className="text-sm text-neutral-600">
                            CIK: {company.cik}
                          </p>
                          {selectedCompany?.cik === company.cik && selectedCompany.industry && (
                            <p className="text-sm text-primary-600 mt-1">
                              Industry: {selectedCompany.industry}
                              {selectedCompany.sicCodes && selectedCompany.sicCodes.length > 0 && ` (SIC: ${selectedCompany.sicCodes.join(', ')})`}
                            </p>
                          )}
                        </div>
                        {selectedCompany?.cik === company.cik && (
                          <CheckCircle2 className="w-6 h-6 text-primary-600" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* India Company Results */}
              {indiaResults.length > 0 && (
                <div className="space-y-3">
                  <p className="text-sm text-neutral-500">
                    Found {indiaResults.length} companies. Click to select.
                  </p>
                  {indiaResults.map((company, idx) => {
                    // The default `/finance/search-companies` no longer enriches
                    // with Moneycontrol probes (too slow for typeahead), so
                    // `sc_id` can be null. Compose a stable identifier from the
                    // fields that *are* present.
                    const compKey = company.sc_id
                      ?? company.sc_nse_symbol
                      ?? company.sc_bse_symbol
                      ?? company.stock_name;
                    const selKey = selectedIndiaCompany?.sc_id
                      ?? selectedIndiaCompany?.sc_nse_symbol
                      ?? selectedIndiaCompany?.sc_bse_symbol
                      ?? selectedIndiaCompany?.stock_name;
                    const isSelected = !!selKey && selKey === compKey;
                    return (
                    <div
                      key={compKey || idx}
                      onClick={() => handleSelectIndiaCompany(company)}
                      className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                        isSelected
                          ? 'border-primary-600 bg-primary-50'
                          : 'border-neutral-200 hover:border-neutral-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-neutral-900">{company.stock_name}</h4>
                          <p className="text-sm text-neutral-600">
                            {company.sc_nse_symbol && <>NSE: <span className="font-mono font-semibold text-primary-600">{company.sc_nse_symbol}</span></>}
                            {company.sc_bse_symbol && <span className="ml-3">BSE: <span className="font-mono text-neutral-500">{company.sc_bse_symbol}</span></span>}
                          </p>
                        </div>
                        {isSelected && (
                          <CheckCircle2 className="w-6 h-6 text-primary-600" />
                        )}
                      </div>
                    </div>
                  );
                  })}
                </div>
              )}

              {/* UK Company Results */}
              {ukResults.length > 0 && (
                <div className="space-y-3">
                  <p className="text-sm text-neutral-500">
                    Found {ukResults.length} UK companies. Click to select.
                  </p>
                  {ukResults.map((company, idx) => (
                    <div
                      key={idx}
                      onClick={() => handleSelectUkCompany(company)}
                      className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                        selectedUkCompany?.company_name === company.company_name
                          ? 'border-primary-600 bg-primary-50'
                          : 'border-neutral-200 hover:border-neutral-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-neutral-900">{company.company_name}</h4>
                          <p className="text-sm text-neutral-600">
                            {company.sector && <span>{company.sector}</span>}
                            {company.years_count > 0 && (
                              <span className="ml-3">{company.years_count} year{company.years_count !== 1 ? 's' : ''} of data</span>
                            )}
                          </p>
                        </div>
                        {selectedUkCompany?.company_name === company.company_name && (
                          <CheckCircle2 className="w-6 h-6 text-primary-600" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Populated Form Fields (read-only preview) */}
              {(selectedCompany || selectedIndiaCompany || selectedUkCompany) && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start gap-2 mb-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-green-900 mb-1">
                        Industry Information Auto-Populated
                      </h4>
                      <p className="text-sm text-green-700">
                        Review the information below before submitting
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium">Industry Name:</span>{' '}
                      {formData.name}
                    </div>
                    <div>
                      <span className="font-medium">SIC Codes:</span>{' '}
                      {formData.sicCodes?.join(', ') || 'None'}
                    </div>
                    <div>
                      <span className="font-medium">NAICS Codes:</span>{' '}
                      {formData.naicsCodes?.join(', ') || 'None'}
                    </div>
                    <div>
                      <span className="font-medium">GICS Code:</span>{' '}
                      {formData.gicsCode || 'None'}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Duplicate Warning */}
          {duplicateWarning && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-yellow-900 mb-1">
                  Potential Duplicate
                </h4>
                <p className="text-sm text-yellow-700">{duplicateWarning}</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {checkOrCreateMutation.isError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-red-900 mb-1">Error</h4>
                <p className="text-sm text-red-700">
                  {checkOrCreateMutation.error instanceof Error
                    ? checkOrCreateMutation.error.message
                    : 'Failed to create industry'}
                </p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-neutral-200">
            <button
              type="button"
              onClick={() => navigate('/industries')}
              className="px-6 py-3 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                checkOrCreateMutation.isPending || !formData.name.trim()
              }
              className="flex-1 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {checkOrCreateMutation.isPending ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Checking...
                </>
              ) : (
                'Create Industry'
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Success Modal */}
      {showSuccessModal && createdIndustry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
            {createdIndustry.existed ? (
              <>
                <div className="text-center mb-6">
                  <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-neutral-900 mb-2">
                    Industry Already Exists
                  </h2>
                  <p className="text-neutral-600">
                    We found an existing industry matching your criteria:{' '}
                    <span className="font-semibold">{createdIndustry.name}</span>
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowSuccessModal(false)}
                    className="flex-1 px-4 py-2 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleViewExisting}
                    className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    View Industry
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="text-center mb-6">
                  <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-neutral-900 mb-2">
                    Industry Stub Created
                  </h2>
                  <p className="text-neutral-600 mb-4">
                    Created stub for{' '}
                    <span className="font-semibold">{createdIndustry.name}</span>
                  </p>
                  <p className="text-sm text-neutral-600">
                    Would you like to generate comprehensive research now (30-60
                    seconds) or do it later?
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleGenerateLater}
                    className="flex-1 px-4 py-2 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50"
                  >
                    Generate Later
                  </button>
                  <button
                    onClick={handleGenerateNow}
                    disabled={generateResearchMutation.isPending}
                    className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {generateResearchMutation.isPending ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      'Generate Now'
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GenerateIndustryResearch;
